import React from 'react';
import { motion } from 'motion/react';
import {
  Camera,
  Calendar,
  CheckSquare,
  Smartphone,
  Sparkles,
  GraduationCap,
  Users,
  Brain,
  TrendingUp,
  Gamepad2,
  Wifi,
  Languages,
  ArrowRight,
} from 'lucide-react';

const features = [
  {
    icon: Camera,
    title: 'Escaneo inteligente',
    description: 'Fotografía tu tarea, syllabus u horario. La IA extrae automáticamente todas las actividades y fechas.',
    color: 'from-violet-500 to-purple-600',
    lightBg: 'from-violet-50 to-purple-50',
    badge: 'IA',
    span: 'md:col-span-2',
  },
  {
    icon: Brain,
    title: 'Ayudante de estudio',
    description: 'Pregunta cualquier duda. La IA te explica conceptos y te guía paso a paso.',
    color: 'from-pink-500 to-rose-500',
    lightBg: 'from-pink-50 to-rose-50',
    badge: 'IA',
    span: '',
  },
  {
    icon: Sparkles,
    title: 'Planes personalizados',
    description: 'Cronogramas de estudio basados en tus entregas, tiempo y dificultad.',
    color: 'from-amber-500 to-orange-500',
    lightBg: 'from-amber-50 to-orange-50',
    badge: 'IA',
    span: '',
  },
  {
    icon: CheckSquare,
    title: 'Gestión de tareas',
    description: 'Organiza todas tus tareas por materia, prioridad y fecha límite con estadísticas claras.',
    color: 'from-blue-500 to-cyan-500',
    lightBg: 'from-blue-50 to-cyan-50',
    span: '',
  },
  {
    icon: Calendar,
    title: 'Calendario integrado',
    description: 'Vista de mes, semana y día con recordatorios automáticos para nunca olvidar nada.',
    color: 'from-green-500 to-emerald-500',
    lightBg: 'from-green-50 to-emerald-50',
    span: 'md:col-span-2',
  },
  {
    icon: GraduationCap,
    title: 'Seguimiento de notas',
    description: 'Registra calificaciones. Promedios, tendencias y predicciones de rendimiento.',
    color: 'from-[#a67eec] to-[#8b6de0]',
    lightBg: 'from-[#a67eec]/10 to-[#8b6de0]/10',
    span: '',
  },
];

const differentiators = [
  {
    icon: Sparkles,
    title: 'IA incluida',
    description: 'Otras apps cobran $10-20/mes extra. En Frappi está incluida.',
    highlight: true,
    color: 'from-[#a67eec] to-[#8b6de0]',
  },
  {
    icon: Languages,
    title: '100% en español',
    description: 'Diseñada para LATAM. Interfaz y soporte nativos.',
    color: 'from-pink-500 to-rose-500',
  },
  {
    icon: Wifi,
    title: 'Funciona offline',
    description: 'Accede sin conexión. Sincroniza cuando tengas internet.',
    color: 'from-cyan-500 to-blue-500',
  },
  {
    icon: Gamepad2,
    title: 'Gamificación',
    description: 'Rachas, logros y recompensas que motivan a estudiar.',
    color: 'from-amber-500 to-orange-500',
  },
];

const benefits = [
  {
    icon: Users,
    title: 'Para estudiantes',
    gradient: 'from-[#a67eec] to-[#8b6de0]',
    lightBg: 'from-[#a67eec]/10 to-[#8b6de0]/10',
    items: [
      'Organiza todas tus tareas en un solo lugar',
      'Mejora tus calificaciones con planes de estudio IA',
      'Reduce el estrés con recordatorios inteligentes',
      'Gana logros y mantén tu racha de productividad',
    ],
  },
  {
    icon: TrendingUp,
    title: 'Para padres de familia',
    gradient: 'from-[#5bb4f2] to-[#5ddba3]',
    lightBg: 'from-violet-50 to-pink-50',
    items: [
      'Agrega todos tus hijos con Premium ilimitado',
      'Vista organizada de tareas por estudiante',
      'Alertas cuando hay tareas próximas a vencer',
      'Seguimiento de notas y progreso académico',
    ],
  },
];

export function Features() {
  return (
    <section id="features" className="py-24 md:py-32 relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-muted/40 via-background to-muted/20" />

      {/* Decorative circles */}
      <div className="absolute -top-40 -right-40 w-80 h-80 border border-primary/5 rounded-full" />
      <div className="absolute -bottom-40 -left-40 w-96 h-96 border border-violet-500/5 rounded-full" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] border border-primary/3 rounded-full" />

      <div className="container relative mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="max-w-3xl mx-auto text-center mb-20"
        >
          <motion.span
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/80 backdrop-blur-sm border border-primary/20 text-primary text-sm font-semibold mb-6 shadow-sm"
          >
            <Sparkles size={14} className="text-amber-500" />
            Funcionalidades
          </motion.span>
          <h2 className="text-3xl md:text-5xl lg:text-6xl font-extrabold text-foreground mb-6 tracking-tight">
            Todo el poder de la IA{' '}
            <span className="bg-gradient-to-r from-[#a67eec] via-[#5bb4f2] to-[#5ddba3] bg-clip-text text-transparent">
              a tu alcance
            </span>
          </h2>
          <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto">
            Frappi combina inteligencia artificial con diseño intuitivo para transformar tu vida académica.
          </p>
        </motion.div>

        {/* Bento Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5 mb-28 max-w-6xl mx-auto">
          {features.map((feature, index) => {
            const actualIndex = index >= 4 ? index + 1 : index;
            return (
              <React.Fragment key={feature.title}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: actualIndex * 0.06 }}
                  whileHover={{ y: -5, scale: 1.01 }}
                  className={`group relative p-7 md:p-8 rounded-3xl bg-white/70 backdrop-blur-sm border border-border/50 hover:border-primary/30 transition-all duration-300 bento-glow ${feature.span}`}
                >
                  {/* Hover gradient background */}
                  <div className={`absolute inset-0 rounded-3xl bg-gradient-to-br ${feature.lightBg} opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none`} />

                  {/* AI Badge */}
                  {feature.badge && (
                    <motion.div
                      initial={{ rotate: -12 }}
                      whileHover={{ rotate: 0, scale: 1.1 }}
                      className="absolute -top-1 -right-1 z-10"
                    >
                      <span className="px-3 py-1.5 rounded-full bg-gradient-to-r from-amber-400 to-amber-500 text-white text-xs font-bold shadow-lg shadow-amber-500/30 flex items-center gap-1">
                        <Sparkles size={10} />
                        {feature.badge}
                      </span>
                    </motion.div>
                  )}

                  {/* Icon */}
                  <div className="relative">
                    <motion.div
                      whileHover={{ rotate: [0, -10, 10, 0], scale: 1.1 }}
                      transition={{ duration: 0.4 }}
                      className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${feature.color} flex items-center justify-center mb-5 shadow-lg group-hover:shadow-xl transition-shadow duration-300`}
                    >
                      <feature.icon className="w-7 h-7 text-white" />
                    </motion.div>
                  </div>

                  <h3 className="relative text-xl font-bold text-foreground mb-3 group-hover:text-primary transition-colors">
                    {feature.title}
                  </h3>
                  <p className="relative text-muted-foreground leading-relaxed">
                    {feature.description}
                  </p>

                  {/* Learn more arrow on hover */}
                  <motion.div
                    initial={{ opacity: 0, x: -10 }}
                    whileHover={{ opacity: 1, x: 0 }}
                    className="relative mt-4 flex items-center gap-1 text-primary text-sm font-semibold opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                  >
                    <span>Conoce más</span>
                    <ArrowRight size={14} />
                  </motion.div>
                </motion.div>

                {/* CTA card after 4th feature (fills the gap in row 2) */}
                {index === 3 && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: 4 * 0.06 }}
                    whileHover={{ y: -5, scale: 1.02 }}
                    className="relative p-7 md:p-8 rounded-3xl overflow-hidden flex flex-col justify-between"
                    style={{
                      background: 'linear-gradient(135deg, #a67eec, #5bb4f2, #5ddba3)',
                    }}
                  >
                    {/* Decorative elements */}
                    <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2" />
                    <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/10 rounded-full translate-y-1/2 -translate-x-1/2" />

                    <div className="relative">
                      <div className="flex items-center gap-2 mb-4">
                        <div className="w-10 h-10 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
                          <Sparkles className="w-5 h-5 text-white" />
                        </div>
                        <span className="text-white/80 text-sm font-medium">7 días gratis</span>
                      </div>

                      <h3 className="text-2xl font-bold text-white mb-2">
                        Prueba todo gratis
                      </h3>
                      <p className="text-white/80 text-sm leading-relaxed mb-6">
                        Accede a todas las funciones con IA incluida. Sin tarjeta requerida.
                      </p>
                    </div>

                    <motion.a
                      href="#pricing"
                      whileHover={{ scale: 1.03 }}
                      whileTap={{ scale: 0.97 }}
                      className="relative inline-flex items-center justify-center gap-2 w-full py-3 px-5 rounded-xl bg-white text-[#a67eec] font-semibold text-sm shadow-lg shadow-black/10 hover:shadow-xl transition-shadow"
                    >
                      <Smartphone className="w-4 h-4" />
                      Comenzar ahora
                      <ArrowRight className="w-4 h-4" />
                    </motion.a>
                  </motion.div>
                )}
              </React.Fragment>
            );
          })}
        </div>

        {/* Differentiators Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="max-w-5xl mx-auto mb-28"
        >
          <div className="text-center mb-14">
            <h3 className="text-2xl md:text-4xl font-extrabold text-foreground mb-4 tracking-tight">
              ¿Por qué elegir Frappi?
            </h3>
            <p className="text-muted-foreground text-lg">
              Diseñada desde cero para estudiantes de Latinoamérica
            </p>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
            {differentiators.map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 15 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.3, delay: index * 0.08 }}
                whileHover={{ y: -5, scale: 1.02 }}
                className={`relative p-5 md:p-6 rounded-2xl text-center overflow-hidden transition-all duration-300 ${
                  item.highlight
                    ? 'bg-gradient-to-br from-primary/5 to-violet-500/5 border-2 border-primary/20 shadow-lg shadow-primary/5'
                    : 'bg-white/70 backdrop-blur-sm border border-border/50 hover:border-primary/20'
                }`}
              >
                {/* Glow effect for highlighted */}
                {item.highlight && (
                  <div className="absolute -inset-px rounded-2xl bg-gradient-to-br from-[#a67eec]/10 to-[#8b6de0]/10 blur-sm" />
                )}

                <motion.div
                  whileHover={{ scale: 1.1, rotate: 5 }}
                  className={`relative w-14 h-14 rounded-2xl mx-auto mb-4 flex items-center justify-center shrink-0 bg-gradient-to-br ${item.color} shadow-lg`}
                >
                  <item.icon className="w-7 h-7 text-white" />
                </motion.div>
                <h4 className="relative font-bold mb-2 text-base md:text-lg text-foreground">
                  {item.title}
                </h4>
                <p className="relative text-sm text-muted-foreground leading-relaxed">
                  {item.description}
                </p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Benefits Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="max-w-5xl mx-auto"
        >
          <div className="text-center mb-14">
            <h3 className="text-2xl md:text-4xl font-extrabold text-foreground mb-4 tracking-tight">
              Diseñado para toda la familia
            </h3>
            <p className="text-muted-foreground text-lg">
              Frappi ayuda a estudiantes a organizarse y a padres a apoyar el éxito académico.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-5">
            {benefits.map((benefit, index) => (
              <motion.div
                key={benefit.title}
                initial={{ opacity: 0, y: 15 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                whileHover={{ y: -4 }}
                className="group relative p-8 md:p-10 rounded-3xl bg-white/70 backdrop-blur-sm border border-border/50 hover:border-primary/20 transition-all duration-300 overflow-hidden"
              >
                {/* Gradient overlay on hover */}
                <div className={`absolute inset-0 rounded-3xl bg-gradient-to-br ${benefit.lightBg} opacity-0 group-hover:opacity-100 transition-opacity duration-500`} />

                <div className="relative flex items-center gap-4 mb-8">
                  <motion.div
                    whileHover={{ rotate: [0, -5, 5, 0] }}
                    className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${benefit.gradient} flex items-center justify-center shadow-lg shrink-0`}
                  >
                    <benefit.icon className="w-7 h-7 text-white" />
                  </motion.div>
                  <h4 className="text-xl md:text-2xl font-bold text-foreground">{benefit.title}</h4>
                </div>
                <ul className="relative space-y-4">
                  {benefit.items.map((item, i) => (
                    <motion.li
                      key={item}
                      initial={{ opacity: 0, x: -10 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.1 + i * 0.05 }}
                      className="flex items-start gap-3 text-muted-foreground"
                    >
                      <div className={`w-6 h-6 rounded-full bg-gradient-to-r ${benefit.gradient} flex items-center justify-center flex-shrink-0 mt-0.5`}>
                        <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      <span className="text-base">{item}</span>
                    </motion.li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}
